import type { PlatformChatApi } from '@liks/backend-svc-api/esm/apis/PlatformChatApi';
import type { PlatformTurn } from '@liks/backend-svc-api/esm/models/index';
import {
  buildSendPlatformMessageDTO,
  type SendPlatformMessageOptions,
} from './platformChatMessageOps';
import {
  appendStreamedText,
  applyStreamedEvent,
} from './platformChatStreamParse';

export async function streamPlatformAssistantReply(
  platformChatApi: PlatformChatApi,
  sessionId: string,
  content: string,
  handlers: {
    onChunk: (chunk: string) => void;
    onFirstChunk?: () => void;
  },
  options?: SendPlatformMessageOptions,
): Promise<PlatformTurn> {
  const response =
    await platformChatApi.deLiksPlatformchatBoundaryPlatformMessageResourceStreamPlatformChatReplyRaw(
      {
        sessionId,
        sendPlatformMessageDTO: buildSendPlatformMessageDTO(content, options),
        ...(options?.maxResults !== undefined
          ? { maxResults: options.maxResults }
          : {}),
        ...(options?.minScore !== undefined
          ? { minScore: options.minScore }
          : {}),
      },
    );

  const stream = response.raw.body;
  if (!stream) {
    throw new Error('Platform chat reply stream returned no body');
  }

  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let pendingLine = '';
  let sawFirstChunk = false;
  let streamedAssistantContent = '';
  let turn: PlatformTurn = {};

  const emitChunk = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '[DONE]') {
      return;
    }

    if (!sawFirstChunk) {
      sawFirstChunk = true;
      handlers.onFirstChunk?.();
    }
    streamedAssistantContent = appendStreamedText(
      streamedAssistantContent,
      trimmed,
    );
    handlers.onChunk(trimmed);
  };

  streamReading: for (;;) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }

    pendingLine += decoder.decode(value, { stream: true });
    const lines = pendingLine.split(/\r?\n/);
    pendingLine = lines.pop() ?? '';

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine === '') {
        continue;
      }

      const payload = trimmedLine.startsWith('data:')
        ? trimmedLine.slice(5).trim()
        : trimmedLine;
      const result = applyStreamedEvent(payload, { turn, emitChunk });
      turn = result.turn;
      if (result.done) {
        break streamReading;
      }
    }
  }

  const trailing = applyStreamedEvent(pendingLine, { turn, emitChunk });
  turn = trailing.turn;

  try {
    await reader.cancel();
  } catch {
    // Stream may already be closed.
  }

  if (
    streamedAssistantContent.length > 0 &&
    (turn.assistantMessage?.content == null ||
      turn.assistantMessage.content.length === 0)
  ) {
    turn = {
      ...turn,
      assistantMessage: {
        ...turn.assistantMessage,
        role: 'ASSISTANT',
        content: streamedAssistantContent,
      },
    };
  }

  return turn;
}
